"use client";
import { useState, useRef, useEffect, MutableRefObject } from "react";

interface SegmentedControlProps {
  onRoleChange: (role: string) => void;
  currentRoleStripe?: string; // Optional, used only if the user is subscribed
}

export default function SegmentedControl({
  onRoleChange,
  currentRoleStripe,
}: SegmentedControlProps) {
  const roles = ["Contractor", "Marina"];
  const initialRole = currentRoleStripe
    ? currentRoleStripe.charAt(0).toUpperCase() + currentRoleStripe.slice(1)
    : roles[0];
  const [selectedRole, setSelectedRole] = useState(initialRole);
  const [indicatorStyles, setIndicatorStyles] = useState({});

  // Explicitly type the ref to be an array of HTMLButtonElement or null
  const buttonRefs: MutableRefObject<(HTMLButtonElement | null)[]> = useRef([]);

  useEffect(() => {
    const handleResize = () => {
      const selectedButton = buttonRefs.current[roles.indexOf(selectedRole)];
      if (selectedButton) {
        const { offsetLeft, offsetWidth } = selectedButton;
        setIndicatorStyles({
          left: offsetLeft,
          width: offsetWidth,
        });
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [selectedRole]);

  return (
    <div className="relative flex w-fit bg-txtSecondary/15 gap-2 items-center justify-center rounded-lg p-2">
      <div
        className="absolute top-1 bottom-1 z-0 transition-all duration-300 bg-white rounded-lg shadow-md"
        style={{ ...indicatorStyles }}
      />

      {roles.map((role, index) => {
        // Only display the role if the user is not subscribed or the role matches their subscription
        if (currentRoleStripe && role.toLowerCase() !== currentRoleStripe)
          return null;

        return (
          <button
            key={index}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            className={`relative z-10 flex items-center mobile:text-xs p-2 flex-1 justify-center rounded-lg transition-colors ${
              selectedRole === role ? "text-black" : "text-gray-500"
            }`}
            onClick={() => {
              setSelectedRole(role);
              onRoleChange(role.toLowerCase().replace(" ", ""));
            }}
          >
            {role}
          </button>
        );
      })}
    </div>
  );
}
